import { Injectable } from '@angular/core';
import type { Attachment, ExchangeIdFormat, FileAttachment, ItemAttachment, MailFolder, Message, Recipient, ReferenceAttachment } from '@microsoft/microsoft-graph-types';
import { GraphAuthService } from '../auth/services/graph-auth.service';

@Injectable({
    providedIn: 'root',
})
export class GraphMailService {
    constructor(private graphAuthService: GraphAuthService) {}

    private mailFolderSearchCache = new Map<string, Promise<MailFolderSearchItem[]>>();

    private getCommaSeperatedListAsRecipients(addresslist: string): Recipient[] {
        return addresslist
            .split('; ')
            .filter((address) => address.length > 3)
            .map((recipient) => {
                return {
                    emailAddress: {
                        address: recipient,
                    },
                };
            });
    }

    public async translateExchangeIds(params: TranslateExchangeIdsParams): Promise<{ value: { sourceId: string; targetId: string }[] }> {
        let apiEndpoint = '/me/translateExchangeIds';

        if (params.userId) {
            apiEndpoint = `/users/${params.userId}/translateExchangeIds`;
        }

        return (await this.graphAuthService.getGraphClient())?.api(apiEndpoint).post({ inputIds: params.inputIds, sourceIdType: params.sourceIdType, targetIdType: params.targetIdType });
    }

    public async sendMessage({ subject, contentType = 'html', content, recipients, ccRecipients = '', bccRecipients = '', saveToSentItems = true, attachments, userId }: { subject: string; contentType: 'html' | 'text'; content: string; recipients: string; ccRecipients: string; bccRecipients: string; saveToSentItems: boolean; attachments?: TTAttachment[]; userId?: string }) {
        const mail: { message: Message; saveToSentItems: boolean } = {
            saveToSentItems: saveToSentItems,
            message: {
                subject: subject,
                body: {
                    contentType: contentType,
                    content: content,
                },
                toRecipients: this.getCommaSeperatedListAsRecipients(recipients),
            },
        };

        if (ccRecipients) {
            mail.message.ccRecipients = this.getCommaSeperatedListAsRecipients(ccRecipients);
        }

        if (bccRecipients) {
            mail.message.bccRecipients = this.getCommaSeperatedListAsRecipients(bccRecipients);
        }

        if (attachments) {
            mail.message.attachments = attachments.map((attachment) => {
                return {
                    '@odata.type': '#microsoft.graph.fileAttachment',
                    name: attachment.fileName,
                    contentType: attachment.contentType,
                    contentBytes: attachment.contentBytes,
                };
            });
        }

        let apiEndpoint = '/me/sendMail';

        if (userId) {
            apiEndpoint = `/users/${userId}/sendMail`;
        }

        return (await this.graphAuthService.getGraphClient())?.api(apiEndpoint).post(mail);
    }

    public async markAsSpam(messageId: string, recipientAddress: string) {
        if (!messageId || !recipientAddress) throw Error('Invalid arguments');
        const message = {
            moveToJunk: true,
        };

        return (await this.graphAuthService.getGraphClient())?.api(`/users/${recipientAddress}/messages/${messageId}/markAsJunk`).version('beta').post(message);
    }

    /**
     * Gets the message with the given message id.
     *
     * @param messageId the id of the message to get.
     * @returns a promise with the message.
     */
    public async getMessage(messageId: string, userId?: string) {
        if (!userId) {
            return (await this.graphAuthService.getGraphClient())?.api(`/me/messages/${messageId}`).get();
        }

        return (await this.graphAuthService.getGraphClient())?.api(`/users/${userId}/messages/${messageId}`).get();
    }

    /**
     * Updates the message with the given message id. Updates only the given parameters with exists in Message type.
     *
     * @param messageId the id of the message to update.
     * @param params the parameters of the message to update.
     * @returns a promise.
     */
    public async updateMessage(messageId: string, params: Partial<Message>, userId?: string) {
        if (!userId) {
            return (await this.graphAuthService.getGraphClient())?.api(`/me/messages/${messageId}`).update(params);
        }

        return (await this.graphAuthService.getGraphClient())?.api(`/users/${userId}/messages/${messageId}`).update(params);
    }

    /**
     * Moved the message with the given message id to the deleted items folder.
     *
     * @param messageId the id of the message to move to deleted items.
     * @returns the message moved.
     */
    public async moveMessageToDeletedItems(messageId: string, userId?: string): Promise<Message> {
        const message = {
            destinationId: 'deleteditems',
        };

        if (!userId) {
            return (await this.graphAuthService.getGraphClient())?.api(`/me/messages/${messageId}/move`).post(message);
        }

        return (await this.graphAuthService.getGraphClient())?.api(`/users/${userId}/messages/${messageId}/move`).post(message);
    }

    /**
     * Moves the message with the given message id to the folder with the given folder id or `well-known` name.
     *
     * @param messageId the id of the message to move.
     * @param folderId the id of the folder to move the message to.
     * @returns
     */
    public async moveMessageToFolder(messageId: string, folderId: string, userId?: string) {
        const message = {
            destinationId: folderId,
        };

        if (!userId) {
            return (await this.graphAuthService.getGraphClient())?.api(`/me/messages/${messageId}/move`).post(message);
        }

        return (await this.graphAuthService.getGraphClient())?.api(`/users/${userId}/messages/${messageId}/move`).post(message);
    }

    /**
     * Deletes the message with the given message id.
     *
     * @param messageId the id of the message to delete.
     * @returns a void promise which resolves if deletion went through.
     */
    public async deleteMessage(messageId: string, userId?: string): Promise<void> {
        if (!userId) {
            return (await this.graphAuthService.getGraphClient())?.api(`/me/messages/${messageId}`).delete();
        }

        return (await this.graphAuthService.getGraphClient())?.api(`/users/${userId}/messages/${messageId}`).delete();
    }

    public async searchMessages({ folderId, limit = 20, userId, filter = '', searchtext = '' }: { folderId?: string; limit: number; userId?: string; filter: string; searchtext: string }) {
        let apiEndpoint = `/me`;

        if (userId) {
            apiEndpoint = `/users/${userId}`;
            //return (await this.graphAuthService.getGraphClient())?.api(`/me/mailFolders/${folderId}/messages`).top(limit).get();
        }

        if (folderId) {
            apiEndpoint += `/mailFolders/${folderId}`;
        }

        return (await this.graphAuthService.getGraphClient())?.api(`${apiEndpoint}/messages`).search(searchtext).filter(filter).top(limit).get();
    }

    /**
     * Retrieves messages from the mailfolder with the given folder id.
     *
     * @param folderId the id of the mailfolder to retreieve message from.
     * @param limit the number of messages to get in the request, defaults to 50.
     * @returns {Promise<{value: Message[]; '@odata.nextLink'?: string; '@odata.context': string}>} object containing values and links to access remaining context and values.
     */
    public async getMessagesFromFolder({ folderId, limit = 20, userId, filter = '', orderBy = '', search = '' }: { folderId: string; limit: number; userId?: string; filter: string; orderBy: string; search: string }): Promise<{ value: Message[]; '@odata.nextLink'?: string; '@odata.context': string }> {
        let apiEndpoint = `/me/mailFolders/${folderId}/messages`;

        if (userId) {
            apiEndpoint = `/users/${userId}/mailFolders/${folderId}/messages`;
            //return (await this.graphAuthService.getGraphClient())?.api(`/me/mailFolders/${folderId}/messages`).top(limit).get();
        }

        if (search) {
            return (await this.graphAuthService.getGraphClient())?.api(apiEndpoint).top(limit).search(search).get();
        }
        let date = new Date();
        date.setFullYear(2000);

        let filters = filter.split(' ');

        return (await this.graphAuthService.getGraphClient())
            ?.api(apiEndpoint)
            .filter(filter)
            .orderby((!!orderBy ? orderBy + ',' : '') + 'sentDateTime desc')
            .top(limit)
            .get();
        //return (await this.graphAuthService.getGraphClient())?.api(`/users/${userId}/mailFolders/${folderId}/messages`).top(limit).filter(filter).get();
    }

    public async getAllMessages({ limit = 20, userId, filter = '', search = '' }: { limit: number; userId?: string; filter: string; search: string }): Promise<{ value: Message[]; '@odata.nextLink'?: string; '@odata.context': string }> {
        let apiEndpoint = `/me/messages`;

        if (userId) {
            apiEndpoint = `/users/${userId}/messages`;
            //return (await this.graphAuthService.getGraphClient())?.api(`/me/mailFolders/${folderId}/messages`).top(limit).get();
        }

        return (await this.graphAuthService.getGraphClient())?.api(apiEndpoint).top(limit).filter(filter).search(search).get();
        //return (await this.graphAuthService.getGraphClient())?.api(`/users/${userId}/mailFolders/${folderId}/messages`).top(limit).filter(filter).get();
    }

    /**
     * Retrieves all the attachments from the message with the given message id.
     *
     * @param messageId the id of the message to retrieve attachments for.
     * @returns a promise containing the a list of attachments and context url.
     */
    public async getAttachmentsFromMessageId(messageId: string, userId?: string): Promise<{ value: (Attachment | FileAttachment | ItemAttachment | ReferenceAttachment)[]; '@odata.context': string }> {
        if (!userId) {
            return (await this.graphAuthService.getGraphClient())?.api(`/me/messages/${messageId}/attachments`).get();
        }

        return (await this.graphAuthService.getGraphClient())?.api(`/users/${userId}/messages/${messageId}/attachments`).get();
    }

    public async getDroppedFileAttachmentFromItemId(params: DroppedOutlookAttachmentParams): Promise<FileAttachment> {
        const attachmentId = await this.translateDroppedAttachmentItemId(params.attachmentItemId, params.userId);
        const messageId = await this.getMessageIdFromDroppedAttachmentItemId(attachmentId, params.userId);

        if (!messageId) {
            throw Error('Could not resolve Outlook attachment message');
        }

        const attachment = await this.getAttachmentFromMessageId(messageId, attachmentId, params.userId);

        if (this.isFileAttachmentWithContentBytes(attachment)) {
            return attachment;
        }

        throw Error(`Outlook attachment ${params.name} has no file content`);
    }

    private async translateDroppedAttachmentItemId(attachmentItemId: string, userId?: string): Promise<string> {
        const translatedIds = await this.translateExchangeIds({
            inputIds: [attachmentItemId],
            sourceIdType: 'ewsId',
            targetIdType: 'restId',
            ...(!!userId ? { userId: userId } : {}),
        });
        const translatedId = translatedIds?.value?.find((exchangeId) => exchangeId.sourceId === attachmentItemId)?.targetId ?? translatedIds?.value?.[0]?.targetId;

        if (!translatedId) {
            throw Error('Could not translate Outlook attachment id');
        }

        return translatedId;
    }

    private async getMessageIdFromDroppedAttachmentItemId(attachmentItemId: string, userId?: string): Promise<string | undefined> {
        if (!userId) {
            const message: Pick<Message, 'id'> = await (await this.graphAuthService.getGraphClient())?.api(`/me/messages/${this.encodeGraphPathSegment(attachmentItemId)}`).select('id').get();
            return message?.id;
        }

        const message: Pick<Message, 'id'> = await (await this.graphAuthService.getGraphClient())?.api(`/users/${userId}/messages/${this.encodeGraphPathSegment(attachmentItemId)}`).select('id').get();
        return message?.id;
    }

    private async getAttachmentFromMessageId(messageId: string, attachmentId: string, userId?: string): Promise<Attachment | FileAttachment | ItemAttachment | ReferenceAttachment> {
        const encodedMessageId = this.encodeGraphPathSegment(messageId);
        const encodedAttachmentId = this.encodeGraphPathSegment(attachmentId);

        if (!userId) {
            return (await this.graphAuthService.getGraphClient())?.api(`/me/messages/${encodedMessageId}/attachments/${encodedAttachmentId}`).get();
        }

        return (await this.graphAuthService.getGraphClient())?.api(`/users/${userId}/messages/${encodedMessageId}/attachments/${encodedAttachmentId}`).get();
    }

    private isFileAttachmentWithContentBytes(attachment: Attachment | FileAttachment | ItemAttachment | ReferenceAttachment): attachment is FileAttachment {
        return 'contentBytes' in attachment && typeof attachment.contentBytes === 'string' && attachment.contentBytes.length > 0;
    }

    private encodeGraphPathSegment(value: string): string {
        return encodeURIComponent(value);
    }

    /**
     * Gets the folder with the given folder-id or `well-known` name.
     *
     * @param folderId id of the folder id to get.
     * @returns a promise containing the folder with the given folder id.
     */
    public async getMailFolder(folderId: string, userId?: string): Promise<MailFolder> {
        if (!userId) {
            return (await this.graphAuthService.getGraphClient())?.api(`/me/mailFolders/${folderId}`).get();
        }

        return (await this.graphAuthService.getGraphClient())?.api(`/users/${userId}/mailFolders/${folderId}`).get();
    }

    public async searchMailFolders({ userId, searchtext, limit = 100 }: SearchMailFoldersParams): Promise<MailFolderSearchItem[]> {
        const query = this.normalizeMailFolderSearchText(searchtext);

        const folders = await this.getSearchableMailFolders(userId);
        const resultLimit = Math.max(0, limit);
        const matchedFolders = query ? folders.filter((folder) => this.mailFolderSearchMatches(folder, query)) : folders;

        return matchedFolders
            .sort((a, b) => {
                const rank = this.getMailFolderSearchRank(a, query) - this.getMailFolderSearchRank(b, query);
                if (rank !== 0) return rank;

                return a.item_name.localeCompare(b.item_name);
            })
            .slice(0, resultLimit);
    }

    public clearMailFolderSearchCache(userId?: string): void {
        if (userId !== undefined) {
            this.mailFolderSearchCache.delete(this.getMailFolderSearchCacheKey(userId));
            return;
        }

        this.mailFolderSearchCache.clear();
    }

    private getMailFolderSearchCacheKey(userId?: string): string {
        return userId || 'me';
    }

    private async getSearchableMailFolders(userId?: string): Promise<MailFolderSearchItem[]> {
        const cacheKey = this.getMailFolderSearchCacheKey(userId);
        let cached = this.mailFolderSearchCache.get(cacheKey);

        if (!cached) {
            cached = this.loadSearchableMailFolders(userId).catch((error) => {
                this.mailFolderSearchCache.delete(cacheKey);
                throw error;
            });

            this.mailFolderSearchCache.set(cacheKey, cached);
        }

        return cached;
    }

    private async loadSearchableMailFolders(userId?: string, folderId?: string, parentPath = ''): Promise<MailFolderSearchItem[]> {
        const folders = folderId ? await this.getAllChildMailFolders({ userId, folderId, top: 200 }) : await this.getAllRootMailFolders({ userId, top: 200 });
        const searchableFolders: MailFolderSearchItem[] = [];

        for (const folder of folders) {
            const displayName = folder.displayName ?? '';
            const itemName = parentPath ? `${parentPath} / ${displayName}` : displayName;
            const item = this.toMailFolderSearchItem(folder, itemName);

            searchableFolders.push(item);

            if ((folder.childFolderCount ?? 0) > 0 && folder.id) {
                searchableFolders.push(...(await this.loadSearchableMailFolders(userId, folder.id, itemName)));
            }
        }

        return searchableFolders;
    }

    private toMailFolderSearchItem(folder: MailFolder, itemName: string): MailFolderSearchItem {
        return {
            ...folder,
            item_id: folder.id ?? '',
            item_keyno: folder.id ?? '',
            item_name: itemName,
            item_id_parent: folder.parentFolderId,
            item_glyphicon: 'fas-folder',
            item_glyphicon_color: 'orange',
        };
    }

    private mailFolderSearchMatches(folder: MailFolderSearchItem, query: string): boolean {
        return this.normalizeMailFolderSearchText(folder.displayName ?? '').includes(query) || this.normalizeMailFolderSearchText(folder.item_name).includes(query);
    }

    private getMailFolderSearchRank(folder: MailFolderSearchItem, query: string): number {
        const name = this.normalizeMailFolderSearchText(folder.displayName ?? '');
        const path = this.normalizeMailFolderSearchText(folder.item_name);

        if (name === query) return 0;
        if (name.startsWith(query)) return 1;
        if (path === query) return 2;
        if (path.startsWith(query)) return 3;
        if (name.includes(query)) return 4;
        if (path.includes(query)) return 5;

        return 6;
    }

    private normalizeMailFolderSearchText(value: string): string {
        return value.trim().toLocaleLowerCase();
    }

    private async getAllRootMailFolders(params?: RootMailFoldersParams): Promise<MailFolder[]> {
        return this.getAllMailFoldersByPage((skip, top) => this.getRootMailFolders({ ...(params ?? {}), skip, top }), params?.top);
    }

    private async getAllChildMailFolders(params: ChildMailFoldersParams): Promise<MailFolder[]> {
        return this.getAllMailFoldersByPage((skip, top) => this.getChildMailFolders({ ...params, skip, top }), params.top);
    }

    private async getAllMailFoldersByPage(getPage: (skip: number, top: number) => Promise<{ value?: MailFolder[]; '@odata.nextLink'?: string }>, top = 200): Promise<MailFolder[]> {
        const pageSize = top > 0 ? top : 200;
        const folders: MailFolder[] = [];
        let skip = 0;
        let hasMore = true;

        while (hasMore) {
            const response = await getPage(skip, pageSize);
            const page = response?.value ?? [];

            folders.push(...page);
            skip += pageSize;
            hasMore = !!response?.['@odata.nextLink'];
        }

        return folders;
    }

    /**
     * Retrieves all folders at every level and maps the MailFolder list to list item objects.
     *
     * @returns {Promise<Object[]>} a promise of all folders at every level as list item objects.
     */
    public async getAllMailFoldersAsItemList(userId?: string): Promise<Object[]> {
        // let mailFolders: MailFolder[] = [];
        // await this.getMailFolders({ folders: mailFolders, userId: userId });
        // return mailFolders.map((folder, index) => {
        //     return {
        //         orderby: index,
        //         isexpanded: '1',
        //         item_id: folder.id,
        //         item_keyno: folder.id,
        //         item_name: `${folder.displayName}${(folder.unreadItemCount ?? 0) > 0 ? ` (${folder.unreadItemCount})` : ''}`,
        //         item_id_parent: folder.parentFolderId,
        //         item_glyphicon: 'fas-folder',
        //         item_glyphicon_color: 'orange',
        //         child_count: folder.childFolderCount,
        //     };
        // });

        return [];
    }

    /**
     * Modifies the given list of folders to include folders of all levels.
     *
     * @param folders list of folders to add mailfolders of every level to.
     * @param folderId the id of a parent folder, used to get child folders.
     */
    public async getMailFolders({ folders, folderId, userId }: { folders: MailFolder[]; folderId?: string; userId?: string }) {
        let endpoint = '/me/mailFolders';

        if (userId) {
            endpoint = `/users/${userId}/mailFolders`;
        }

        if (folderId && !userId) {
            endpoint = `/me/mailFolders/${folderId}/childFolders`;
        } else if (folderId && userId) {
            endpoint = `/users/${userId}/mailFolders/${folderId}/childFolders`;
        }

        const response = await (await this.graphAuthService.getGraphClient())?.api(endpoint).top(200).get();

        for (let folder of response.value) {
            folders.push(folder);

            if (folder.childFolderCount > 0) {
                await this.getMailFolders({ folders: folders, folderId: folder.id, userId: userId });
            }
        }
    }

    public async getRootMailFolders(params?: RootMailFoldersParams) {
        let endpoint = '/me/mailFolders';

        if (params?.userId) {
            endpoint = `/users/${params.userId}/mailFolders`;
        }

        return await (
            await this.graphAuthService.getGraphClient()
        )
            ?.api(endpoint)
            .skip(params?.skip ?? 0)
            .top(params?.top ?? 200)
            .get();
    }

    public async getChildMailFolders(params: ChildMailFoldersParams) {
        let endpoint = '';

        if (params.folderId && !params.userId) {
            endpoint = `/me/mailFolders/${params.folderId}/childFolders`;
        } else if (params.folderId && params.userId) {
            endpoint = `/users/${params.userId}/mailFolders/${params.folderId}/childFolders`;
        }

        return await (
            await this.graphAuthService.getGraphClient()
        )
            ?.api(endpoint)
            .skip(params.skip ?? 0)
            .top(params.top ?? 200)
            .get();
    }

    public async moveMailFolder(params: MoveMailFolderParams) {
        let endpoint = '';

        if (params.id && !params.userId) {
            endpoint = `/me/mailFolders/${params.id}/move`;
        } else if (params.id && params.userId) {
            endpoint = `/users/${params.userId}/mailFolders/${params.id}/move`;
        }

        const response = await (await this.graphAuthService.getGraphClient())?.api(endpoint).post({ destinationId: params.destinationId });

        this.clearMailFolderSearchCache(params.userId);

        return response;
    }

    public async getMailList(accessToken: string) {
        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append('Authorization', bearer);

        const options = {
            method: 'GET',
            headers: headers,
        };

        const response = await fetch('https://graph.microsoft.com/v1.0/me/messages', options);

        console.log('messages');
        console.dir(response);

        let client = await response.json();

        console.dir(client);
        return client;
    }
}

interface TTAttachment {
    fileName: string;
    contentType: string;
    contentBytes: string;
}

export interface RootMailFoldersParams {
    top?: number;
    skip?: number;
    userId?: string;
}

export interface ChildMailFoldersParams extends RootMailFoldersParams {
    folderId: string;
}

export interface SearchMailFoldersParams {
    userId?: string;
    searchtext: string;
    limit?: number;
}

export interface MailFolderSearchItem extends MailFolder {
    item_id: string;
    item_keyno: string;
    item_name: string;
    item_id_parent?: string | null;
    item_glyphicon: string;
    item_glyphicon_color: string;
}

export interface MoveMailFolderParams {
    userId?: string;

    /**
     * The id of the new parent folder.
     */
    destinationId: string;

    /**
     * The id of the folder to move.
     */
    id: string;
}

export interface DroppedOutlookAttachmentParams {
    attachmentItemId: string;
    name: string;
    size?: number;
    contentType?: string;
    userId?: string;
}

export interface TranslateExchangeIdsParams {
    inputIds: string[];
    sourceIdType: ExchangeIdFormat;
    targetIdType: ExchangeIdFormat;
    userId?: string;
}
